import React, { useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Badge from '@cloudscape-design/components/badge';
import Link from '@cloudscape-design/components/link';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Box from '@cloudscape-design/components/box';
import Modal from '@cloudscape-design/components/modal';
import RadioGroup from '@cloudscape-design/components/radio-group';
import { useNavigate } from 'react-router-dom';
import { TemplateMatchInfo, ClassificationMode } from '../services/api';

export interface TemplateMatchBannerProps {
  templateMatchInfo: TemplateMatchInfo | null | undefined;
  onRerun: (mode: ClassificationMode) => void;
  rerunLoading: boolean;
}

const TemplateMatchBanner: React.FC<TemplateMatchBannerProps> = ({
  templateMatchInfo,
  onRerun,
  rerunLoading,
}) => {
  const navigate = useNavigate();
  const [showRerunModal, setShowRerunModal] = useState(false);
  const [selectedMode, setSelectedMode] = useState<ClassificationMode>('hybrid');

  const rerunButton = (
    <Button
      onClick={() => setShowRerunModal(true)}
      loading={rerunLoading}
      data-testid="rerun-button"
    >
      Re-run
    </Button>
  );

  const rerunModal = (
    <Modal
      visible={showRerunModal}
      onDismiss={() => setShowRerunModal(false)}
      header="Re-run job"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setShowRerunModal(false)}>Cancel</Button>
            <Button
              variant="primary"
              loading={rerunLoading}
              onClick={() => { onRerun(selectedMode); setShowRerunModal(false); }}
            >
              Re-run
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box>Choose how the agent should classify and map this file:</Box>
        <RadioGroup
          value={selectedMode}
          onChange={({ detail }) => setSelectedMode(detail.value as ClassificationMode)}
          items={[
            {
              value: 'keyword',
              label: 'Keyword',
              description: 'Deterministic keyword matching. Fast, no LLM cost. Best for standard broker files.',
            },
            {
              value: 'hybrid',
              label: 'Hybrid (recommended)',
              description: 'Keyword first, then LLM for low-confidence rows. Best balance of speed and accuracy.',
            },
            {
              value: 'llm',
              label: 'Full LLM',
              description: 'All rows classified by Claude. Highest accuracy, highest Bedrock cost.',
            },
          ]}
        />
      </SpaceBetween>
    </Modal>
  );

  // No template match info or LLM-only mapping
  if (!templateMatchInfo || templateMatchInfo.mappingSource === 'llm') {
    return (
      <>
        <Alert type="info" data-testid="template-match-banner-llm" action={rerunButton}>
          All columns mapped by LLM
        </Alert>
        {rerunModal}
      </>
    );
  }

  // LLM re-run (template was overridden)
  if (templateMatchInfo.mappingSource === 'llm-rerun') {
    return (
      <>
        <Alert type="info" data-testid="template-match-banner-rerun" action={rerunButton}>
          Columns re-mapped by LLM (template override)
        </Alert>
        {rerunModal}
      </>
    );
  }

  // Template was used
  const similarityPercent = Math.round(templateMatchInfo.similarity * 100);
  const matchedCount = templateMatchInfo.matchedColumns.length;
  const deltaCount = templateMatchInfo.deltaColumns.length;

  return (
    <>
      <Alert
        type="info"
        data-testid="template-match-banner-template"
        action={rerunButton}
      >
        <SpaceBetween size="xs">
          <SpaceBetween direction="horizontal" size="xs">
            <span>Template applied</span>
            <Badge color="green">{similarityPercent}% match</Badge>
            <Link
              onFollow={(e) => {
                e.preventDefault();
                navigate('/library');
              }}
              data-testid="template-library-link"
            >
              {templateMatchInfo.templateId}
            </Link>
          </SpaceBetween>
          <SpaceBetween direction="horizontal" size="xs">
            <Box variant="small">
              <Badge color="blue">{matchedCount} matched</Badge>
            </Box>
            <Box variant="small">
              <Badge color="grey">{deltaCount} delta (LLM)</Badge>
            </Box>
          </SpaceBetween>
          {(matchedCount > 0 || deltaCount > 0) && (
            <ExpandableSection headerText="Column details" variant="footer">
              <SpaceBetween size="xs">
                {matchedCount > 0 && (
                  <div>
                    <Box variant="awsui-key-label">Matched from template ({matchedCount})</Box>
                    <Box variant="small" color="text-body-secondary">
                      {templateMatchInfo.matchedColumns.join(', ')}
                    </Box>
                  </div>
                )}
                {deltaCount > 0 && (
                  <div>
                    <Box variant="awsui-key-label">Sent to LLM ({deltaCount})</Box>
                    <Box variant="small" color="text-body-secondary">
                      {templateMatchInfo.deltaColumns.join(', ')}
                    </Box>
                  </div>
                )}
              </SpaceBetween>
            </ExpandableSection>
          )}
        </SpaceBetween>
      </Alert>
      {rerunModal}
    </>
  );
};

export default TemplateMatchBanner;
